/* eslint-disable unicorn/no-array-callback-reference, unicorn/no-array-method-this-argument */
import { NotFoundException } from '@nestjs/common';
import { instanceToInstance } from 'class-transformer';
import { isEmpty } from 'lodash';
import { Document, FilterQuery } from 'mongoose';

import { PusherEventType } from '../enumerations';
import { BaseOutputModel, CreateInputModel, DeleteInputModel, InfoInputModel, ListInputModel, ListOutputModel, UpdateInputModel } from '../models';
import { BaseRepository } from '../repositories';
import { QueryOptions, Request } from '../types';
import { PusherService } from './pusher.service';

export abstract class BaseService<T extends Document, K extends BaseOutputModel> {
  protected constructor(
    public readonly baseRepository: BaseRepository<T>,
    public readonly basePusherService: PusherService,
    public readonly outputModelInstantiator: (document: T) => K,
    public readonly instanceName: string,
    public readonly searchProperties: (keyof T)[] = [],
    public readonly prerequisites?: FilterQuery<T>
  ) {}

  public async create(createInputModel: CreateInputModel, request: Request, { populateOptions }: QueryOptions<T> = {}): Promise<K> {
    const object = { ...createInputModel, ...this.prerequisites, created_by: request.user?.id || request.body?.id, deleted: false };
    const document = await this.baseRepository.create(object, { populateOptions });
    const data = instanceToInstance(this.outputModelInstantiator(document));
    return this.basePusherService.emit(data, PusherEventType.Created, this.instanceName, request);
  }

  public async delete(deleteInputModel: DeleteInputModel, request: Request): Promise<void> {
    const document = await this.baseRepository.findByIdAndDelete(deleteInputModel.id, deleteInputModel.force, { ...this.prerequisites, deleted: false });
    if (isEmpty(document)) throw new NotFoundException('Document not found.');
    const data = instanceToInstance(this.outputModelInstantiator(document));
    await this.basePusherService.emit(data, PusherEventType.Deleted, this.instanceName, request);
  }

  public async info(infoInputModel: InfoInputModel, filter: FilterQuery<T> = {}, { populateOptions, projection }: QueryOptions<T> = {}): Promise<K> {
    const query = { ...filter, ...this.prerequisites, deleted: false };
    const document = await this.baseRepository.findById(infoInputModel.id, query, { projection, populateOptions });
    if (isEmpty(document)) throw new NotFoundException('Document not found.');
    return instanceToInstance(this.outputModelInstantiator(document));
  }

  public async list(
    listInputModel: ListInputModel,
    filter: FilterQuery<T> = {},
    { populateOptions, projection }: QueryOptions<T> = {}
  ): Promise<ListOutputModel<K>> {
    const query = { ...filter, ...this.prerequisites, deleted: false };
    if (!isEmpty(this.searchProperties) && !isEmpty(listInputModel.search))
      query.$or = this.searchProperties.map((property) => ({ [property]: new RegExp(listInputModel.search as string, 'i') })) as any;
    const [count, documents] = await Promise.all([
      this.baseRepository.count(query),
      this.baseRepository.find(query, { ...listInputModel, populateOptions, projection }),
    ]);
    const models = documents.map((document) => instanceToInstance(this.outputModelInstantiator(document)));
    return new ListOutputModel(count, models);
  }

  public async update(updateInputModel: UpdateInputModel, request: Request, { populateOptions, projection, upsert = false }: QueryOptions<T> = {}): Promise<K> {
    const filter = { ...this.prerequisites, deleted: false };
    const object = { ...updateInputModel, updated_by: request.user?.id || request.body?.id };
    const document = await this.baseRepository.findByIdAndUpdate(updateInputModel.id, object, filter, { populateOptions, projection, upsert });
    if (isEmpty(document)) throw new NotFoundException('Document not found.');
    const data = instanceToInstance(this.outputModelInstantiator(document));
    return this.basePusherService.emit(data, PusherEventType.Updated, this.instanceName, request);
  }
}
